import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import ReactDOM from 'react-dom'

// Free-text combobox with an in-page suggestion list.
// Why it exists: on iPad, Safari shows <datalist> suggestions in the keyboard bar,
// about 3 at a time, so a long cost-code list is unusable there. This draws its own list.
// Why a portal: the board cell popup (.modal, max-height 85vh) and its crew list
// (.worker-list-box, max-height 140px) both scroll, so a list positioned inside them
// gets clipped. The list is rendered into document.body, position: fixed, and placed
// from the input's screen position (re-placed on scroll and resize).
// Typing is always allowed; picking a row just fills in that row's value.

const MAX_ROWS = 75 // more than this bogs down the iPad; typing narrows it
const LIST_MAX_H = 280
const Z_LIST = 1100 // above .modal-backdrop (100) and the app's other overlays (1000)

const fold = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

// wrapStyle: style for the outer wrapper (e.g. { flex: 1 } inside a .modal-row).
export default function SearchCombo({ value, onChange, items, placeholder, className, inputStyle, title, minListWidth = 260, wrapStyle }) {
  const [open, setOpen] = useState(false)
  const [hi, setHi] = useState(-1)
  const [pos, setPos] = useState(null)
  const boxRef = useRef(null)
  const inputRef = useRef(null)
  const listRef = useRef(null)
  const text = value ?? ''

  const matches = useMemo(() => {
    const toks = fold(text).split(/\s+/).filter(Boolean)
    const all = items || []
    if (!toks.length) return all
    return all.filter(it => {
      const hay = fold(`${it.value} ${it.label || ''} ${it.sub || ''}`)
      return toks.every(t => hay.includes(t))
    })
  }, [items, text])
  const shown = matches.slice(0, MAX_ROWS)
  const more = matches.length - shown.length
  // Row Enter would pick: the arrowed row, else (while typing) the exact code or the first match.
  const exactIdx = shown.findIndex(it => String(it.value) === String(text).trim())
  const active = hi >= 0 ? hi : text.trim() ? (exactIdx >= 0 ? exactIdx : 0) : -1

  const place = useCallback(() => {
    const el = inputRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const width = Math.min(Math.max(r.width, minListWidth), vw - 16)
    const left = Math.max(8, Math.min(r.left, vw - width - 8))
    const below = vh - r.bottom
    if (below < 200 && r.top > below) {
      setPos({ left, width, bottom: vh - r.top + 2, maxHeight: Math.min(LIST_MAX_H, r.top - 8) })
    } else {
      setPos({ left, width, top: r.bottom + 2, maxHeight: Math.min(LIST_MAX_H, below - 8) })
    }
  }, [minListWidth])

  // While open: follow the input when the page, the modal or the crew list scrolls,
  // and close on a tap anywhere outside both the input and the list.
  useEffect(() => {
    if (!open) return
    place()
    const away = (e) => {
      if (boxRef.current && boxRef.current.contains(e.target)) return
      if (listRef.current && listRef.current.contains(e.target)) return
      setOpen(false)
    }
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    document.addEventListener('mousedown', away)
    document.addEventListener('touchstart', away)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
      document.removeEventListener('mousedown', away)
      document.removeEventListener('touchstart', away)
    }
  }, [open, place])

  const openList = () => {
    if (open) return
    setHi(-1)
    setOpen(true)
    // Touch screens: keep the input clear of the on-screen keyboard.
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
      setTimeout(() => {
        if (inputRef.current && inputRef.current.scrollIntoView) inputRef.current.scrollIntoView({ block: 'center' })
        place()
      }, 300)
    }
  }

  const pick = (it) => {
    onChange(it.value)
    setOpen(false)
    if (inputRef.current) inputRef.current.blur() // lets the iPad keyboard drop
  }
  const moveTo = (i) => {
    setHi(i)
    const el = listRef.current && listRef.current.children[i]
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
  }
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) openList(); else moveTo(Math.min(active + 1, shown.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); if (open) moveTo(Math.max(active - 1, 0)) }
    else if (e.key === 'Enter') { if (open && shown[active]) { e.preventDefault(); pick(shown[active]) } }
    else if (e.key === 'Escape' || e.key === 'Tab') { setOpen(false) }
  }

  const list = open && pos && ReactDOM.createPortal(
    <div ref={listRef} style={{ position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom,
      maxHeight: pos.maxHeight, overflowY: 'auto', WebkitOverflowScrolling: 'touch', zIndex: Z_LIST,
      background: 'var(--surface)', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius)',
      boxShadow: '0 6px 18px rgba(0,0,0,.14)', fontFamily: 'var(--font)' }}>
      {shown.map((it, i) => {
        const current = String(it.value) === String(text).trim()
        return (
          <div key={it.key ?? it.value}
            onMouseDown={e => e.preventDefault()} onClick={() => pick(it)} onMouseEnter={() => setHi(i)}
            style={{ minHeight: 44, boxSizing: 'border-box', padding: '6px 12px', display: 'flex', flexDirection: 'column',
              justifyContent: 'center', cursor: 'pointer', borderBottom: '0.5px solid var(--border)',
              background: i === active ? 'var(--blue-bg)' : current ? 'var(--surface2)' : 'var(--surface)' }}>
            <span style={{ fontSize: 13, fontWeight: current ? 600 : 500, color: 'var(--text)' }}>{it.label || it.value}</span>
            {it.sub ? <span style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{it.sub}</span> : null}
          </div>
        )
      })}
      {more > 0 && (
        <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--text3)' }}>Keep typing to narrow — {more} more</div>
      )}
      {matches.length === 0 && (
        <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text3)' }}>No match — what you typed will be kept</div>
      )}
    </div>,
    document.body
  )

  return (
    <div ref={boxRef} style={{ display: 'flex', minWidth: 0, ...wrapStyle }}>
      <input ref={inputRef} className={className} value={text} placeholder={placeholder} title={title}
        autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
        onFocus={openList}
        onChange={e => { onChange(e.target.value); setHi(-1); if (!open) openList() }}
        onKeyDown={onKey}
        style={{ width: '100%', ...inputStyle }} />
      {list}
    </div>
  )
}
